import type { OrderArticlePrice } from '@/services/api/types';

export type PackageId = 'mill' | 'basic' | 'pro';

export interface PackageDef {
  readonly id: PackageId;
  readonly articleCategory: string;
  readonly featureCount: number;
  readonly variantValues: readonly string[];
  readonly defaultVariantIndex: number;
}

export const PACKAGES: readonly PackageDef[] = [
  {
    id: 'mill',
    articleCategory: 'MDentureMill',
    featureCount: 2,
    variantValues: ['unimax_mill_ok', 'unimax_mill_uk', 'bimax_mill'],
    defaultVariantIndex: 0,
  },
  {
    id: 'basic',
    articleCategory: 'MDentureBasic',
    featureCount: 7,
    variantValues: ['bimaxillaer'],
    defaultVariantIndex: 0,
  },
  {
    id: 'pro',
    articleCategory: 'MDenturePro',
    featureCount: 10,
    variantValues: ['unimax_ok', 'unimax_uk', 'bimaxillaer'],
    defaultVariantIndex: 2,
  },
];

export const PACKAGE_CATEGORIES = PACKAGES.map((pkg) => pkg.articleCategory);

export interface PackageVariantView {
  value: string;
  label: string;
  priceLabel: string;
  handlingTime?: number;
}

export interface SelectedPackageSelection {
  packageId: PackageId;
  selectedVariantValue: string;
  variants: readonly PackageVariantView[];
}

const STORAGE_KEY = 'mdenture:selected-package-selection';

export function articleValueOf(article: OrderArticlePrice): string {
  return article.productID;
}

function isPackageId(value: unknown): value is PackageId {
  return value === 'mill' || value === 'basic' || value === 'pro';
}

function isVariantView(value: unknown): value is PackageVariantView {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.value === 'string' &&
    typeof candidate.label === 'string' &&
    typeof candidate.priceLabel === 'string' &&
    (candidate.handlingTime === undefined || typeof candidate.handlingTime === 'number')
  );
}

export function isSelectedPackageSelection(value: unknown): value is SelectedPackageSelection {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  if (!isPackageId(candidate.packageId) || typeof candidate.selectedVariantValue !== 'string') {
    return false;
  }

  return Array.isArray(candidate.variants) && candidate.variants.every(isVariantView);
}

export function saveSelectedPackageSelection(selection: SelectedPackageSelection): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(selection));
}

export function loadSelectedPackageSelection(): SelectedPackageSelection | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const raw = window.sessionStorage.getItem(STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(raw);
    return isSelectedPackageSelection(parsed) ? parsed : null;
  } catch {
    return null;
  }
}
